/**
 * Cloudflare Turnstile Component
 *
 * A privacy-friendly CAPTCHA alternative that protects forms from bots.
 * Only renders if TURNSTILE_SITE_KEY is configured.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Load Turnstile script once
 */
const loadTurnstileScript = () =>
  new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`,
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.turnstile));
      existingScript.addEventListener("error", reject);
      return;
    }

    // Load script
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Failed to load Turnstile"));

    document.head.appendChild(script);
  });

/**
 * Turnstile CAPTCHA Component
 *
 * @param {Object} props
 * @param {function} props.onVerify - Callback when verification succeeds, receives token
 * @param {function} props.onError - Callback when verification fails
 * @param {function} props.onExpire - Callback when token expires
 * @param {'auto'|'light'|'dark'} props.theme - Widget theme
 * @param {'normal'|'compact'} props.size - Widget size
 * @param {string} props.className - Additional CSS classes
 */
export const Turnstile = ({
  onVerify,
  onError,
  onExpire,
  theme = "auto",
  size = "normal",
  className = "",
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch security config to get site key
  const { data: securityConfig } = useQuery({
    queryKey: ["security-config"],
    queryFn: async () => {
      try {
        const response = await api.get("/auth/security/config");
        return response.data?.data;
      } catch {
        return null;
      }
    },
    staleTime: Infinity, // Config doesn't change
    retry: false,
  });

  const siteKey = securityConfig?.turnstileSiteKey;

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const initWidget = async () => {
      try {
        const turnstile = await loadTurnstileScript();

        if (!mounted || !containerRef.current) return;

        // Remove existing widget if any
        if (widgetIdRef.current) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch {
            // Ignore removal errors
          }
        }

        // Render widget
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => {
            onVerify?.(token);
          },
          "error-callback": () => {
            setError("Verification failed");
            onError?.();
          },
          "expired-callback": () => {
            onExpire?.();
          },
        });

        setIsLoading(false);
      } catch (err) {
        console.error("Turnstile init error:", err);
        setError(err.message);
        setIsLoading(false);
        onError?.();
      }
    };

    initWidget();

    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, [siteKey, theme, size, onVerify, onError, onExpire]);

  // Don't render if Turnstile is not configured
  if (!siteKey) {
    return null;
  }

  if (error) {
    return (
      <div className={`text-sm text-destructive ${className}`}>
        Security verification unavailable
      </div>
    );
  }

  return (
    <div className={className}>
      {isLoading && (
        <div className="h-16.25 flex items-center justify-center text-sm text-muted-foreground">
          Loading security check...
        </div>
      )}
      <div ref={containerRef} className={isLoading ? "hidden" : ""} />
    </div>
  );
};
